/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ButtonGroup
} from '@chakra-ui/react'
import { IoMdClose, IoMdCheckmark } from 'react-icons/io'

const DeleteModal = ({
  isOpen,
  onClose,
  item,
  batchId
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState(null)

  useEffect(() => {
    if (isOpen && item) {
      setStatus(null)
      setIsLoading(false)
    }
  }, [isOpen, item])

  const handleDelete = async () => {
    if (!item) return

    try {
      setIsLoading(true)
      onClose(true)
    } catch (error) {
      console.error('Error deleting item:', error)
      setStatus('error')
    }
  }

  const handleCancel = () => {
    onClose(false)
  }

  const renderContent = () => {
    if (!item) return null

    if (isLoading && !status) {
      return <p>Deleting...</p>
    }

    if (status) {
      const messages = {
        success: 'The item was deleted successfully.',
        error: 'Something went wrong. Please try again or contact support.'
      }

      const icons = {
        success: <IoMdCheckmark />,
        error: <IoMdClose />
      }

      const colors = {
        success: 'green',
        error: 'red'
      }

      const currentColor = colors[status]

      return (
        <div style={{ color: currentColor, textAlign: 'center' }}>
          <div style={{
            borderColor: currentColor,
            borderWidth: '2px',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            fontSize: '24px'
          }}>
            {icons[status]}
          </div>
          <div>
            {messages[status]}
          </div>
        </div>
      )
    }

    return (
      <>
        <p>
          Are you sure you want to delete this {item.aiMetadataLicenseTerms?.work?.title ? 'asset' : 'item'}?
        </p>
        <p className="text-sm mt-2 text-gray-600">
          {item.aiMetadataLicenseTerms?.work?.title || item.title || item.name || `Item #${item.id}`} will be permanently removed.
        </p>
        <ButtonGroup gap='4' mt='4'>
          <Button
            colorScheme="gray"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            colorScheme="red"
          >
            Delete
          </Button>
        </ButtonGroup>
      </>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Asset</ModalHeader>
        <ModalCloseButton onClick={handleCancel} />
        <ModalBody pb={6}>
          {renderContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default DeleteModal
